import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaCalendarAlt } from '@react-icons/all-files/fa/FaCalendarAlt';
import { FaEdit } from '@react-icons/all-files/fa/FaEdit';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import { FaSpinner } from '@react-icons/all-files/fa/FaSpinner';
import { FaTrash } from '@react-icons/all-files/fa/FaTrash';
import ProfaseModal from '../../commons/ProfaseModal';
import Confirm, { useConfirm } from '../../commons/Confirm';
import type { AlertVariant, ToastOptions } from '../../commons/PageAlert';
import {
  formatTipoPeriodoLabel,
  formatWorkflowPeriodicidadLabel,
  normalizeWorkflowPeriodicidad,
} from '../../shared/workflow-periodicidad';
import {
  workflowPeriodoActividadesUrl,
  type PeriodoActividadWorkflowPeriodo,
} from '../../shared/workflow-periodo';
import {
  buildPeriodoActividadPayload,
  defaultPeriodoActividadFormValues,
  formatPeriodoActividadDataLabel,
  periodoActividadApiErrorMessage,
  periodoActividadFormFromEntity,
  type PeriodoActividadFormValues,
  type ProfasePeriodoActividadEntity,
  validatePeriodoActividadForm,
} from '../../shared/profase-periodo-actividad';
import PeriodoActividadFormFields from './periodo-actividad-form-fields';

type ShowToast = (
  variant: AlertVariant,
  message: string,
  durationOrOptions?: number | ToastOptions,
) => void;

interface WorkflowCalendarioPeriodoModalProps {
  open: boolean;
  workflowId: number;
  workflowNombre?: string;
  workflowPeriodicidad?: string | null;
  onClose: () => void;
  showToast: ShowToast;
}

type FormMode = { kind: 'create' } | { kind: 'edit'; id: number };

function extractApiErrorMessage(error: unknown, fallback: string): string {
  const err = error as {
    response?: { data?: { detail?: string; message?: string; title?: string; errorKey?: string } };
    message?: string;
  };
  const data = err?.response?.data;
  const errorKey = data?.errorKey;
  if (errorKey === 'periodicidadincompatible') {
    return 'Algún periodo actividad no es compatible con la periodicidad del workflow.';
  }
  if (errorKey === 'periodoactividadnotfound') {
    return 'Algún periodo actividad ya no existe en el catálogo.';
  }
  if (errorKey === 'idnotfound') {
    return 'Workflow no encontrado.';
  }
  return periodoActividadApiErrorMessage(error, fallback);
}

function NumeroPeriodoLabel({ numero }: { numero?: number | null }) {
  if (numero == null) {
    return (
      <span style={{ color: 'var(--crm-text-muted)', fontSize: 12, fontStyle: 'italic' }}>
        Todos
      </span>
    );
  }
  return <span>{numero}</span>;
}

export default function WorkflowCalendarioPeriodoModal({
  open,
  workflowId,
  workflowNombre,
  workflowPeriodicidad,
  onClose,
  showToast,
}: WorkflowCalendarioPeriodoModalProps) {
  const [items, setItems] = useState<PeriodoActividadWorkflowPeriodo[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [periodoForm, setPeriodoForm] = useState<PeriodoActividadFormValues>(() =>
    defaultPeriodoActividadFormValues(),
  );
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof PeriodoActividadFormValues, string>>>({});
  const [submittingForm, setSubmittingForm] = useState(false);
  const [loadingFormEntity, setLoadingFormEntity] = useState(false);
  const [openingEditId, setOpeningEditId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  const workflowTipoPeriodo = normalizeWorkflowPeriodicidad(workflowPeriodicidad);

  const formBusy = submittingForm || loadingFormEntity;
  const modalBusy = saving || formBusy || deletingId != null;

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get<PeriodoActividadWorkflowPeriodo[]>(
        workflowPeriodoActividadesUrl(workflowId),
      );
      const data = Array.isArray(res.data) ? res.data : [];
      setItems(data);
      setSelectedIds(new Set(data.filter(i => i.asignado).map(i => i.id)));
    } catch (error) {
      showToast('error', extractApiErrorMessage(error, 'No se pudo cargar el calendario del workflow.'));
      setItems([]);
      setSelectedIds(new Set());
    } finally {
      setLoading(false);
    }
  }, [showToast, workflowId]);

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setFormMode(null);
    setFormErrors({});
    setPeriodoForm(defaultPeriodoActividadFormValues(workflowTipoPeriodo));
  }, [workflowTipoPeriodo]);

  useEffect(() => {
    if (!open || !workflowId) return;
    closeForm();
    void loadItems();
  }, [closeForm, loadItems, open, workflowId]);

  const habilitadosCount = useMemo(
    () => items.filter(i => i.habilitado).length,
    [items],
  );

  const selectedCount = selectedIds.size;

  const toggleSelected = (item: PeriodoActividadWorkflowPeriodo) => {
    if (!item.habilitado) return;
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.add(item.id);
      return next;
    });
  };

  const selectAllHabilitados = () => {
    setSelectedIds(new Set(items.filter(i => i.habilitado).map(i => i.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleClose = () => {
    if (modalBusy) return;
    dismiss();
    onClose();
  };

  useEffect(() => {
    setConfirmLoading(deletingId != null);
  }, [deletingId, setConfirmLoading]);

  const handleDelete = async (item: PeriodoActividadWorkflowPeriodo) => {
    setDeletingId(item.id);
    try {
      await axios.delete(`api/profase-periodo-actividads/${item.id}`);
      dismiss();
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      await loadItems();
      showToast(
        'success',
        `Definición "${item.nombre ?? `#${item.id}`}" eliminada correctamente.`,
      );
    } catch (error) {
      showToast('error', extractApiErrorMessage(error, 'No se pudo eliminar la definición de periodo.'));
    } finally {
      setDeletingId(null);
    }
  };

  const confirmDelete = (item: PeriodoActividadWorkflowPeriodo) => {
    ask(
      <>
        ¿Confirmas que deseas eliminar{' '}
        <strong>{item.nombre ?? `definición #${item.id}`}</strong>? Esta acción no se puede deshacer.
      </>,
      () => { void handleDelete(item); },
      { title: 'Eliminar definición de periodo', confirmLabel: 'Eliminar', variant: 'danger' },
    );
  };

  const handleApply = async () => {
    setSaving(true);
    try {
      const res = await axios.put<PeriodoActividadWorkflowPeriodo[]>(
        workflowPeriodoActividadesUrl(workflowId),
        { periodoActividadIds: Array.from(selectedIds) },
      );
      const data = Array.isArray(res.data) ? res.data : [];
      setItems(data);
      setSelectedIds(new Set(data.filter(i => i.asignado).map(i => i.id)));
      showToast('success', 'Calendario del workflow actualizado.');
      onClose();
    } catch (error) {
      showToast('error', extractApiErrorMessage(error, 'No se pudo guardar el calendario del workflow.'));
    } finally {
      setSaving(false);
    }
  };

  const openCreateForm = () => {
    if (formOpen && formMode?.kind === 'create') {
      closeForm();
      return;
    }
    setFormOpen(true);
    setFormMode({ kind: 'create' });
    setFormErrors({});
    setPeriodoForm(defaultPeriodoActividadFormValues(workflowTipoPeriodo));
  };

  const openEditForm = async (item: PeriodoActividadWorkflowPeriodo) => {
    setFormOpen(true);
    setFormMode({ kind: 'edit', id: item.id });
    setFormErrors({});
    setOpeningEditId(item.id);
    setLoadingFormEntity(true);
    try {
      const res = await axios.get<ProfasePeriodoActividadEntity>(`api/profase-periodo-actividads/${item.id}`);
      setPeriodoForm(periodoActividadFormFromEntity(res.data));
    } catch (error) {
      showToast('error', extractApiErrorMessage(error, 'No se pudo cargar la definición de periodo.'));
      closeForm();
    } finally {
      setLoadingFormEntity(false);
      setOpeningEditId(null);
    }
  };

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validatePeriodoActividadForm(periodoForm);
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      return;
    }

    const editId = formMode?.kind === 'edit' ? formMode.id : undefined;
    setSubmittingForm(true);
    try {
      const payload = buildPeriodoActividadPayload(periodoForm, editId);

      if (editId != null) {
        await axios.put(`api/profase-periodo-actividads/${editId}`, payload);
        showToast('success', 'Definición de periodo actualizada.');
        closeForm();
        await loadItems();
      } else {
        const created = await axios.post<{ id?: number }>('api/profase-periodo-actividads', payload);
        const createdId = created.data?.id;
        const res = await axios.get<PeriodoActividadWorkflowPeriodo[]>(
          workflowPeriodoActividadesUrl(workflowId),
        );
        const data = Array.isArray(res.data) ? res.data : [];
        setItems(data);
        if (createdId != null) {
          setSelectedIds(prev => {
            const next = new Set(prev);
            const row = data.find(i => i.id === createdId);
            if (row?.habilitado) next.add(createdId);
            return next;
          });
        } else {
          setSelectedIds(new Set(data.filter(i => i.asignado).map(i => i.id)));
        }
        showToast('success', 'Definición de periodo creada. Confirma con Aplicar para asignarla.');
        closeForm();
      }
    } catch (error) {
      showToast(
        'error',
        extractApiErrorMessage(error, editId != null
          ? 'No se pudo actualizar la definición de periodo.'
          : 'No se pudo crear la definición de periodo.'),
      );
    } finally {
      setSubmittingForm(false);
    }
  };

  return (
    <ProfaseModal
      open={open}
      titleId="workflow-calendario-periodo-modal-title"
      size="wide"
      scroll
      onBackdropMouseDown={e => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <Confirm config={confirmConfig} />
      <div style={s.header}>
        <h2 id="workflow-calendario-periodo-modal-title" className="profase-modal-title m-0">
          <FaCalendarAlt style={{ marginRight: 8, verticalAlign: '-2px' }} />
          Calendario workflow
        </h2>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm"
          onClick={handleClose}
          disabled={modalBusy}
        >
          Cancelar
        </button>
      </div>

      <p style={s.description}>
        Asocia definiciones de periodo actividad a <strong>{workflowNombre || `workflow #${workflowId}`}</strong>.
        {' '}
        Periodicidad del workflow: <strong>{formatWorkflowPeriodicidadLabel(workflowPeriodicidad)}</strong>.
        {' '}
        Solo se pueden asignar periodos con el mismo tipo (
        {workflowTipoPeriodo || 'sin periodicidad'}
        ).
      </p>

      <div style={s.bulkActions}>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm"
          onClick={openCreateForm}
          disabled={loading || modalBusy}
        >
          <FaPlus style={{ marginRight: 6, verticalAlign: '-2px' }} />
          {formOpen && formMode?.kind === 'create' ? 'Ocultar formulario' : 'Nueva definición'}
        </button>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm"
          onClick={selectAllHabilitados}
          disabled={loading || modalBusy || habilitadosCount === 0}
        >
          Asignar todos habilitados
        </button>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm"
          onClick={deselectAll}
          disabled={loading || modalBusy || selectedCount === 0}
        >
          Desasignar todos
        </button>
      </div>

      {formOpen ? (
        <form style={s.createForm} onSubmit={handleSubmitForm}>
          <div style={s.formTitle}>
            {formMode?.kind === 'edit'
              ? `Editar definición #${formMode.id}`
              : 'Nueva definición de periodo'}
            {loadingFormEntity ? (
              <FaSpinner className="fa-spin" style={{ marginLeft: 8 }} aria-hidden />
            ) : null}
          </div>
          <PeriodoActividadFormFields
            form={periodoForm}
            errors={formErrors}
            disabled={formBusy || modalBusy}
            workflowTipoPeriodo={workflowTipoPeriodo}
            onChange={setPeriodoForm}
            onClearError={key => setFormErrors(prev => ({ ...prev, [key]: undefined }))}
          />
          <div style={s.createActions}>
            <button
              type="button"
              className="profase-form-btn-secondary btn-sm"
              onClick={closeForm}
              disabled={formBusy || modalBusy}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="profase-form-btn-primary btn-sm"
              disabled={formBusy || modalBusy || loadingFormEntity}
            >
              {submittingForm ? <FaSpinner className="fa-spin" style={{ marginRight: 6 }} /> : null}
              {formMode?.kind === 'edit' ? 'Guardar cambios' : 'Crear definición'}
            </button>
          </div>
        </form>
      ) : null}

      <div style={s.tableWrap}>
        {loading ? (
          <div style={s.loadingCell} role="status" aria-live="polite">
            <FaSpinner className="fa-spin" style={{ marginRight: 8 }} />
            Cargando periodos actividad…
          </div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={{ ...s.th, width: 52, textAlign: 'center' }}>Asignar</th>
                <th style={{ ...s.th, width: 72 }}>ID</th>
                <th style={s.th}>Nombre</th>
                <th style={{ ...s.th, width: 110 }}>Tipo</th>
                <th style={{ ...s.th, width: 100 }}>Número</th>
                <th style={s.th}>Referencia</th>
                <th style={{ ...s.th, width: 100 }}>Estado</th>
                <th style={{ ...s.th, width: 76, textAlign: 'center' }} aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={8} style={s.emptyCell}>Sin definiciones de periodo actividad</td>
                </tr>
              ) : items.map(item => {
                const checked = selectedIds.has(item.id);
                const incompatible = !item.habilitado;
                const rowBusy = saving || deletingId === item.id || openingEditId === item.id;
                return (
                  <tr
                    key={item.id}
                    style={incompatible ? s.rowIncompatible : undefined}
                    title={
                      incompatible
                        ? `Incompatible con periodicidad ${formatWorkflowPeriodicidadLabel(workflowPeriodicidad)}`
                        : undefined
                    }
                  >
                    <td style={{ ...s.td, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={incompatible || rowBusy || modalBusy}
                        onChange={() => toggleSelected(item)}
                        aria-label={`Asignar ${item.nombre ?? `periodo #${item.id}`}`}
                      />
                    </td>
                    <td style={s.td}>{item.id}</td>
                    <td style={s.td}>{item.nombre ?? '—'}</td>
                    <td style={s.td}>{formatTipoPeriodoLabel(item.tipoPeriodo)}</td>
                    <td style={s.td}>
                      <NumeroPeriodoLabel numero={item.numeroPeriodo} />
                    </td>
                    <td style={{ ...s.td, fontSize: 12, color: 'var(--crm-text-secondary)' }}>
                      {formatPeriodoActividadDataLabel(item.data, item.tipoPeriodo)}
                    </td>
                    <td style={s.td}>
                      {item.asignado ? (
                        <span className="workflow-calendario-badge workflow-calendario-badge--assigned">
                          Asignado
                        </span>
                      ) : incompatible ? (
                        <span className="workflow-calendario-badge workflow-calendario-badge--incompatible">
                          Incompatible
                        </span>
                      ) : (
                        <span className="workflow-calendario-badge workflow-calendario-badge--available">
                          Disponible
                        </span>
                      )}
                    </td>
                    <td style={{ ...s.td, textAlign: 'center' }}>
                      <div style={s.rowActions}>
                        <button
                          type="button"
                          style={{
                            ...s.editBtn,
                            ...(rowBusy || modalBusy ? s.iconBtnDisabled : {}),
                          }}
                          title="Editar definición de periodo"
                          aria-label={`Editar ${item.nombre ?? `periodo #${item.id}`}`}
                          disabled={rowBusy || modalBusy}
                          onClick={() => { void openEditForm(item); }}
                        >
                          {openingEditId === item.id
                            ? <FaSpinner className="fa-spin" size={13} />
                            : <FaEdit size={13} />}
                        </button>
                        <button
                          type="button"
                          style={{
                            ...s.deleteBtn,
                            ...(rowBusy || modalBusy ? s.iconBtnDisabled : {}),
                          }}
                          title="Eliminar definición de periodo"
                          aria-label={`Eliminar ${item.nombre ?? `periodo #${item.id}`}`}
                          disabled={rowBusy || modalBusy}
                          onClick={() => confirmDelete(item)}
                        >
                          {deletingId === item.id
                            ? <FaSpinner className="fa-spin" size={13} />
                            : <FaTrash size={13} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div style={s.footer}>
        <span style={s.summary}>
          {selectedCount > 0
            ? `${selectedCount} seleccionado${selectedCount === 1 ? '' : 's'}`
            : 'Ninguno seleccionado'}
          {' · '}
          {habilitadosCount} compatible{habilitadosCount === 1 ? '' : 's'} con el workflow
        </span>
        <div style={s.footerActions}>
          <button
            type="button"
            className="profase-form-btn-secondary btn-sm"
            onClick={handleClose}
            disabled={modalBusy}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="profase-form-btn-primary btn-sm"
            onClick={() => { void handleApply(); }}
            disabled={loading || modalBusy}
          >
            {saving ? <FaSpinner className="fa-spin" style={{ marginRight: 6 }} /> : null}
            Aplicar
          </button>
        </div>
      </div>
    </ProfaseModal>
  );
}

const s: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  description: {
    margin: '0 0 12px',
    fontSize: 13,
    color: 'var(--crm-text-secondary)',
    lineHeight: 1.45,
  },
  bulkActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  createForm: {
    marginBottom: 12,
    padding: 12,
    border: '1px solid var(--crm-border, #e2e8f0)',
    borderRadius: 8,
    background: 'var(--crm-surface-muted, #f8fafc)',
  },
  formTitle: {
    marginBottom: 12,
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--crm-text-primary)',
    display: 'flex',
    alignItems: 'center',
  },
  createActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 12,
  },
  tableWrap: {
    maxHeight: 'min(420px, 55vh)',
    overflow: 'auto',
    border: '1px solid var(--crm-border, #e2e8f0)',
    borderRadius: 8,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
  },
  th: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    padding: '8px 10px',
    textAlign: 'left',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--crm-text-secondary)',
    background: 'var(--crm-surface-muted)',
    borderBottom: '1px solid var(--crm-border)',
  },
  td: {
    padding: '8px 10px',
    fontSize: 13,
    color: 'var(--crm-body-text)',
    borderBottom: '1px solid var(--crm-border)',
    verticalAlign: 'middle',
  },
  rowIncompatible: {
    opacity: 0.55,
    background: 'var(--crm-surface-muted)',
  },
  rowActions: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  editBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    padding: 0,
    border: '1px solid var(--crm-border)',
    borderRadius: 6,
    background: 'var(--crm-input-bg)',
    color: 'var(--crm-text-secondary)',
    cursor: 'pointer',
  },
  deleteBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    padding: 0,
    border: '1px solid var(--crm-form-danger-border)',
    borderRadius: 6,
    background: 'var(--crm-form-danger-bg)',
    color: 'var(--crm-form-danger-text)',
    cursor: 'pointer',
  },
  iconBtnDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
  },
  emptyCell: {
    padding: 20,
    textAlign: 'center',
    color: 'var(--crm-text-secondary)',
    fontSize: 13,
  },
  loadingCell: {
    padding: 24,
    textAlign: 'center',
    color: 'var(--crm-text-secondary)',
    fontSize: 13,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 16,
    flexWrap: 'wrap',
  },
  summary: {
    fontSize: 13,
    color: 'var(--crm-text-secondary)',
  },
  footerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginLeft: 'auto',
  },
};
